import { BUSINESS_LEGAL, formatBusinessAddress, formatBusinessLegalFooter } from '@/lib/businessLocation';
import { CONTACT_PHONE_DISPLAY } from '@/lib/contactDetails';
import { COOKIE_POLICY_PATH } from '@/lib/cookieConsent';

export const LEGAL_EFFECTIVE_DATE = '2026-09-28';
export const LEGAL_CONTACT_EMAIL = 'contact@figtures.com';
export const KVKK_PATH = '/kvkk-aydinlatma-metni';
export const PRIVACY_PATH = '/privacy';
export const TERMS_PATH = '/terms';
export const DELIVERY_REFUND_PATH = '/delivery-cancellation-refund';
export const DISTANCE_SALES_PATH = '/distance-sales-agreement';

export const PAYMENT_PROVIDER = {
  brand: 'iyzico',
  legalName: 'İyzi Ödeme ve Elektronik Para Hizmetleri A.Ş.',
} as const;

export type LegalSection = {
  id?: string;
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

export function legalContactLines(lang: 'tr' | 'en'): string[] {
  const address = formatBusinessAddress(lang);
  if (lang === 'tr') {
    return [
      `${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName})`,
      `${BUSINESS_LEGAL.taxOffice} Vergi Dairesi · VKN ${BUSINESS_LEGAL.vkn}`,
      address,
      CONTACT_PHONE_DISPLAY,
    ];
  }
  return [
    `${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName})`,
    `${BUSINESS_LEGAL.taxOffice} Tax Office · VKN ${BUSINESS_LEGAL.vkn}`,
    address,
    CONTACT_PHONE_DISPLAY,
  ];
}

const ADDR = formatBusinessAddress('tr');
const ADDR_EN = formatBusinessAddress('en');
const PROVIDER_LINE_TR = `${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}), ${ADDR}, ${BUSINESS_LEGAL.taxOffice} Vergi Dairesi, VKN ${BUSINESS_LEGAL.vkn}, e-posta ${LEGAL_CONTACT_EMAIL}, telefon ${CONTACT_PHONE_DISPLAY}`;
const PROVIDER_LINE_EN = `${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}), ${ADDR_EN}, ${BUSINESS_LEGAL.taxOffice} Tax Office, tax ID ${BUSINESS_LEGAL.vkn}, email ${LEGAL_CONTACT_EMAIL}, phone ${CONTACT_PHONE_DISPLAY}`;

export function getKvkkSections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        id: 'controller',
        heading: 'Veri sorumlusu',
        paragraphs: [
          `6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca figtures.com, teklif, iletişim, satış ve sözleşme süreçleri bakımından veri sorumlusu ${BUSINESS_LEGAL.legalName}'tir (${BUSINESS_LEGAL.tradeName}). Adres: ${ADDR}. E-posta: ${LEGAL_CONTACT_EMAIL}.`,
          'Müşteri adına geliştirilen ve müşterinin kendi kullanıcılarına ait verilerin işlendiği ürünlerde, tarafların veri sorumlusu ve veri işleyen rolleri proje sözleşmesi ve gerektiğinde veri işleme sözleşmesiyle ayrıca belirlenir.',
        ],
      },
      {
        id: 'scope',
        heading: 'İşlenen kişisel veriler',
        paragraphs: [
          'Site ziyaretçileri, iletişim formu gönderenler, teklif isteyenler, müşteri temsilcileri ve ödeme yapan kişilere ait aşağıdaki veriler işlenir:',
        ],
        bullets: [
          'Kimlik: ad, soyad; ticari müşterilerde şirket unvanı ve görev',
          'İletişim: e-posta adresi, telefon numarası, fatura ve yazışma adresi',
          'Müşteri işlem: proje talebi, teklif, sözleşme, teslim, onay ve destek kayıtları',
          'Finans: fatura bilgileri, işlem tutarı, ödeme referansı ve ödeme sonucu (kart numarası ve CVV Figtures tarafından alınmaz)',
          'İşlem güvenliği: IP adresi, tarayıcı bilgisi, erişim ve güvenlik kayıtları',
          'Pazarlama ve ölçüm: çerez tercihleri ile yalnızca açık rıza verilmişse analitik ve reklam ölçüm verileri',
          'AI araçları: ücretsiz hesaplama ve analiz araçlarına sizin girdiğiniz proje metinleri',
        ],
      },
      {
        id: 'purpose',
        heading: 'Toplama yöntemi, amaçlar ve hukuki sebepler',
        paragraphs: [
          'Veriler; iletişim formu, e-posta, telefon, teklif ve sözleşme belgeleri, ödeme kuruluşunun işlem bildirimi, çerezler ve sunucu kayıtları yoluyla, kısmen otomatik ve kısmen otomatik olmayan yöntemlerle toplanır.',
          'Talebinizi yanıtlamak, teklif hazırlamak, sözleşmeyi kurmak ve ifa etmek, teslim ve destek sağlamak için KVKK m.5/2(c) (sözleşmenin kurulması veya ifası) hukuki sebebine dayanılır.',
          'Fatura düzenleme, muhasebe kayıtları ve resmî makamlara bildirim için KVKK m.5/2(ç) (hukuki yükümlülük) hukuki sebebine dayanılır.',
          'Site güvenliği, kötüye kullanımın önlenmesi ve olası uyuşmazlıklarda hakların korunması için KVKK m.5/2(e) (bir hakkın tesisi, kullanılması veya korunması) ve m.5/2(f) (meşru menfaat) hukuki sebeplerine dayanılır.',
          'Zorunlu olmayan analitik ve pazarlama çerezleri yalnızca KVKK m.5/1 uyarınca verdiğiniz açık rızaya dayanılarak çalışır. İletişim formunu göndermek, pazarlama iletisi almaya izin verdiğiniz anlamına gelmez.',
        ],
      },
      {
        id: 'transfer',
        heading: 'Kişisel verilerin aktarılması',
        paragraphs: [
          'Kişisel veriler, yukarıdaki amaçlarla sınırlı ve gerekli olduğu ölçüde aşağıdaki alıcı gruplarına aktarılır:',
        ],
        bullets: [
          `Ödeme hizmeti: ${PAYMENT_PROVIDER.legalName} (${PAYMENT_PROVIDER.brand}); kartlı ödemenin gerçekleştirilmesi, dolandırıcılığın önlenmesi ve ödeme mevzuatından doğan yükümlülükler için. iyzico bu işlemler için kendi aydınlatma metni kapsamında ayrıca veri sorumlusudur.`,
          'Barındırma ve altyapı: Amazon Web Services (web sitesi barındırma ve sunucu işlemleri), Supabase (site içerik yönetimi), Cloudflare (bot ve spam koruması).',
          'İletişim: Google Workspace (kurumsal e-posta), EmailJS (iletişim formunun e-postaya iletilmesi).',
          'Ölçüm ve AI araçları: Google Analytics ve Google Tag Manager (yalnızca açık rıza ile), Google Gemini (AI araçlarına girdiğiniz metnin işlenmesi).',
          'Mali müşavirlik ve gerektiğinde hukuki danışmanlık hizmeti alınan kişiler.',
          'Kanunen yetkili kamu kurum ve kuruluşları ile yargı mercileri, talep halinde ve mevzuatın izin verdiği ölçüde.',
        ],
      },
      {
        id: 'abroad',
        heading: 'Yurt dışına aktarım',
        paragraphs: [
          'Google, Amazon Web Services, Supabase, Cloudflare ve EmailJS hizmetlerinin sunucuları Türkiye dışında bulunabildiğinden, bu hizmetler üzerinden işlenen veriler yurt dışına aktarılmış olur.',
          "Bu aktarımlar KVKK m.9 uyarınca; yeterlilik kararı bulunan ülkelere yapılan aktarımlarda bu karara, bulunmayan hallerde Kişisel Verileri Koruma Kurulu'nun ilan ettiği standart sözleşmelere dayanılarak yapılır. Standart sözleşme bulunmayan tekil ve süreklilik arz etmeyen aktarımlarda KVKK m.9/6'da sayılan arızi aktarım hallerinden biri (örneğin sözleşmenin ifası için zorunluluk veya riskler hakkında bilgilendirilerek verdiğiniz açık rıza) aranır.",
        ],
      },
      {
        id: 'retention',
        heading: 'Saklama süreleri',
        bullets: [
          'Sözleşmeye dönüşmeyen iletişim ve teklif kayıtları: son yazışmadan itibaren 2 yıl',
          'Sözleşme, fatura, ödeme ve muhasebe kayıtları: ilgili takvim yılının sonundan itibaren 10 yıl (6102 sayılı Türk Ticaret Kanunu ve 213 sayılı Vergi Usul Kanunu)',
          'Mesafeli sözleşme, ön bilgilendirme ve onay kayıtları: sözleşme kayıtlarıyla birlikte 10 yıl',
          'Sunucu erişim ve güvenlik kayıtları: 2 yıl',
          'Analitik ölçüm verileri: Google Analytics hesap ayarında belirlenen süre, en fazla 14 ay',
          'Çerez tercihiniz: tarayıcınızda, siz değiştirene veya tarayıcı verilerini silene kadar',
        ],
        paragraphs: [
          'Süre sona erdiğinde veriler, Kişisel Verilerin Silinmesi, Yok Edilmesi veya Anonim Hale Getirilmesi Hakkında Yönetmelik uyarınca silinir, yok edilir veya anonim hale getirilir. Açık rızanın geri alınması, başka bir hukuki sebeple saklanması zorunlu olan kayıtların silinmesi sonucunu doğurmaz.',
        ],
      },
      {
        id: 'rights',
        heading: 'Haklarınız ve başvuru yolu',
        paragraphs: [
          'KVKK m.11 uyarınca; kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse buna ilişkin bilgi talep etme, işlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme, yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme, eksik veya yanlış işlenmişse düzeltilmesini isteme, KVKK m.7 çerçevesinde silinmesini veya yok edilmesini isteme, bu işlemlerin aktarıldığı üçüncü kişilere bildirilmesini isteme, münhasıran otomatik sistemlerle analiz edilmesi sonucu aleyhinize bir sonuç çıkmasına itiraz etme ve kanuna aykırı işleme nedeniyle zarara uğramanız halinde zararın giderilmesini talep etme haklarına sahipsiniz.',
          `Başvurunuzu, kimliğinizi tespit edici bilgilerle birlikte, ${ADDR} adresine yazılı olarak veya sistemimizde kayıtlı e-posta adresinizden ${LEGAL_CONTACT_EMAIL} adresine iletebilirsiniz. Başvurular, niteliğine göre en kısa sürede ve en geç 30 gün içinde ücretsiz olarak sonuçlandırılır; işlemin ayrıca bir maliyet gerektirmesi halinde Kurul'ca belirlenen tarife uygulanabilir.`,
        ],
      },
      {
        id: 'cookies',
        heading: 'Çerezler',
        paragraphs: [
          'Çerez kategorileri, kullanılan araçlar ve tercihlerinizi nasıl değiştireceğiniz Çerez Politikası sayfasında açıklanır. Tercihlerinizi dilediğiniz zaman sayfanın altındaki "Çerez Ayarları" bağlantısından değiştirebilirsiniz.',
        ],
      },
    ];
  }

  return [
    {
      id: 'controller',
      heading: 'Data controller',
      paragraphs: [
        `Under Turkish Law No. 6698 on the Protection of Personal Data ("KVKK"), the data controller for figtures.com and for inquiry, sales, and contract processes is ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}), ${ADDR_EN}, ${LEGAL_CONTACT_EMAIL}.`,
        'For products built for a client in which the client’s own users’ data is processed, controller and processor roles are defined in the project agreement and, where needed, a data processing agreement.',
      ],
    },
    {
      id: 'scope',
      heading: 'Personal data we process',
      bullets: [
        'Identity: name and surname; company name and role for business clients',
        'Contact: email, phone, billing and correspondence address',
        'Customer transaction: project request, proposal, contract, delivery, approval, and support records',
        'Finance: billing details, amount, payment reference, and payment outcome (Figtures never receives card numbers or CVV)',
        'Security: IP address, browser data, access and security logs',
        'Marketing and measurement: cookie preferences and, only with consent, analytics and advertising data',
        'AI tools: project text you enter into our free calculators and analysis tools',
      ],
    },
    {
      id: 'purpose',
      heading: 'Collection methods, purposes, and legal bases',
      paragraphs: [
        'Data is collected through the contact form, email, phone, proposal and contract documents, payment notifications from the payment institution, cookies, and server logs.',
        'Responding to requests, preparing proposals, concluding and performing contracts, delivery, and support: KVKK Art. 5/2(c).',
        'Invoicing, bookkeeping, and notifications to authorities: KVKK Art. 5/2(ç) (legal obligation).',
        'Site security, abuse prevention, and protection of rights in disputes: KVKK Art. 5/2(e) and 5/2(f).',
        'Non-essential analytics and marketing cookies run only with your explicit consent under KVKK Art. 5/1. Submitting the contact form does not constitute marketing consent.',
      ],
    },
    {
      id: 'transfer',
      heading: 'Recipients',
      bullets: [
        `Payment: ${PAYMENT_PROVIDER.legalName} (${PAYMENT_PROVIDER.brand}), to process card payments, prevent fraud, and meet payment regulations. iyzico acts as an independent controller under its own privacy notice.`,
        'Hosting and infrastructure: Amazon Web Services (hosting), Supabase (content management), Cloudflare (bot and spam protection).',
        'Communication: Google Workspace (business email), EmailJS (delivering contact form messages).',
        'Measurement and AI tools: Google Analytics and Google Tag Manager (consent only), Google Gemini (processing text you enter into AI tools).',
        'Accountants and, where needed, legal advisors.',
        'Competent public authorities and courts, on request and as permitted by law.',
      ],
    },
    {
      id: 'abroad',
      heading: 'Cross-border transfers',
      paragraphs: [
        'Servers of Google, Amazon Web Services, Supabase, Cloudflare, and EmailJS may be located outside Turkey, so data processed through them is transferred abroad.',
        'Such transfers rely on an adequacy decision where one exists and otherwise on the standard contractual clauses published by the Turkish Personal Data Protection Board, as required by KVKK Art. 9. Occasional, non-recurring transfers not covered by standard clauses rely on one of the incidental transfer grounds in KVKK Art. 9/6.',
      ],
    },
    {
      id: 'retention',
      heading: 'Retention periods',
      bullets: [
        'Inquiries that do not lead to a contract: 2 years from the last correspondence',
        'Contracts, invoices, payment and accounting records: 10 years from the end of the relevant calendar year',
        'Distance contract, pre-contract information, and consent records: 10 years together with contract records',
        'Server access and security logs: 2 years',
        'Analytics data: as configured in Google Analytics, up to 14 months',
        'Your cookie choice: in your browser until you change it or clear browser data',
      ],
    },
    {
      id: 'rights',
      heading: 'Your rights',
      paragraphs: [
        `Under KVKK Art. 11 you may request access, information, correction, deletion or destruction, notification of third parties, object to adverse results of solely automated analysis, and claim compensation for unlawful processing. Send your request with identifying details to ${ADDR_EN} in writing or from your registered email to ${LEGAL_CONTACT_EMAIL}. Requests are answered free of charge within 30 days at the latest.`,
      ],
    },
    {
      id: 'authority',
      heading: 'Authoritative language',
      paragraphs: [
        'The Turkish version of this notice prevails. This English version is provided for convenience.',
      ],
    },
  ];
}

export function getPrivacySections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        id: 'intro',
        heading: 'Kapsam',
        paragraphs: [
          `Bu politika, figtures.com ziyaretçilerinin ve Figtures müşterilerinin kişisel verilerinin nasıl korunduğunu açıklar. Hizmet sağlayıcı ${BUSINESS_LEGAL.legalName}'tir (${BUSINESS_LEGAL.tradeName}). Verilerin hangi amaç ve hukuki sebeple işlendiği, kimlere aktarıldığı ve ne kadar saklandığı KVKK Aydınlatma Metni'nde ayrıntılı olarak yer alır.`,
          'İletişim ve proje bilgileriniz talebinizi yanıtlamak, teklif hazırlamak ve sizinle görüşmek için kullanılır. Sözleşme kurulursa teslim, faturalama, ödeme takibi ve destek kayıtları tutulur. Verileriniz satılmaz, kiralanmaz ve izniniz olmadan pazarlama amacıyla kullanılmaz.',
        ],
      },
      {
        id: 'payment',
        heading: 'Ödeme güvenliği',
        paragraphs: [
          `Kartlı ödemeler, Türkiye Cumhuriyet Merkez Bankası lisanslı ödeme ve elektronik para kuruluşu ${PAYMENT_PROVIDER.legalName} (${PAYMENT_PROVIDER.brand}) altyapısı üzerinden, 256 bit SSL şifrelemesi ve 3D Secure doğrulamasıyla gerçekleştirilir.`,
          'Kart numarası, son kullanma tarihi ve CVV bilgileri doğrudan iyzico tarafından işlenir; Figtures bu bilgileri görmez, almaz ve saklamaz. Figtures tarafında yalnızca işlem referansı, tutar ve ödeme sonucu görülür.',
        ],
      },
      {
        id: 'security',
        heading: 'Bilgi güvenliği',
        paragraphs: [
          'Site trafiği SSL sertifikasıyla şifrelenir. Kişisel verilere erişim görev gereği ihtiyaç duyan kişilerle sınırlandırılır ve yetkisiz erişime karşı uygun teknik ve idari tedbirler alınır. Hizmet sağlayıcılarımız da gizlilik ve güvenlik yükümlülüğü altındadır.',
          'Olası bir veri ihlalinde KVKK m.12 uyarınca Kişisel Verileri Koruma Kurulu ve etkilenen kişiler mevzuatta öngörülen sürede bilgilendirilir.',
        ],
      },
      {
        id: 'client-data',
        heading: 'Müşteri verisi içeren projeler',
        paragraphs: [
          'Proje kapsamında paylaşılan belgelerde çalışan, müşteri veya son kullanıcı verileri bulunuyorsa, bu verileri paylaşma yetkiniz olduğunu teyit etmeniz ve güvenli bir kanal kullanmanız gerekir.',
          'Müşteri verisi üzerinde çalışılan projelerde veri güvenliği tedbirleri, alt işleyenler, ihlal bildirimi ve proje sonunda verilerin iadesi veya silinmesi yazılı sözleşmeyle düzenlenir.',
        ],
      },
      {
        id: 'minors',
        heading: 'Çocukların verileri',
        paragraphs: [
          'Hizmetlerimiz 18 yaşını doldurmuş kişilere ve işletmelere yöneliktir. 18 yaşından küçüklere ait verileri bilerek toplamayız.',
        ],
      },
      {
        id: 'cookies',
        heading: 'Çerezler ve güncellemeler',
        paragraphs: [
          'Çerezlerin türü ve tercihlerinizi nasıl yöneteceğiniz Çerez Politikası sayfasında açıklanır.',
          'Bu politika değiştiğinde güncel metin bu sayfada yayımlanır ve yürürlük tarihi güncellenir.',
        ],
      },
    ];
  }

  return [
    {
      id: 'intro',
      heading: 'Scope',
      paragraphs: [
        `This policy explains how personal data of figtures.com visitors and Figtures clients is protected. The service provider is ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}). Purposes, legal bases, recipients, and retention are detailed in the KVKK Clarification Text.`,
        'Contact and project details are used to respond, prepare proposals, and communicate. If a contract is concluded, we keep delivery, billing, payment, and support records. We do not sell or rent your data or use it for marketing without your consent.',
      ],
    },
    {
      id: 'payment',
      heading: 'Payment security',
      paragraphs: [
        `Card payments are processed by ${PAYMENT_PROVIDER.legalName} (${PAYMENT_PROVIDER.brand}), a payment and e-money institution licensed by the Central Bank of the Republic of Türkiye, using 256-bit SSL encryption and 3D Secure.`,
        'Card number, expiry date, and CVV are handled directly by iyzico; Figtures never sees or stores them. We only see the transaction reference, amount, and outcome.',
      ],
    },
    {
      id: 'security',
      heading: 'Information security',
      paragraphs: [
        'Site traffic is encrypted with SSL. Access to personal data is limited to people who need it, with appropriate technical and organizational safeguards. Our service providers are bound by confidentiality and security obligations.',
        'In case of a data breach, the Board and affected individuals are notified within the period required by KVKK Art. 12.',
      ],
    },
    {
      id: 'client-data',
      heading: 'Projects involving third-party data',
      paragraphs: [
        'If project files contain employee, customer, or end-user data, confirm you are authorized to share it and use a secure channel. Security measures, subprocessors, breach notification, and return or deletion at project end are set out in the written agreement.',
      ],
    },
    {
      id: 'minors',
      heading: 'Children',
      paragraphs: ['Our services are intended for adults and businesses. We do not knowingly collect data from anyone under 18.'],
    },
    {
      id: 'cookies',
      heading: 'Cookies and updates',
      paragraphs: [
        'Cookie types and preference controls are described in our Cookie Policy. When this policy changes, the current version and effective date are published on this page.',
      ],
    },
  ];
}

export function getCookieSections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        heading: 'Veri sorumlusu',
        paragraphs: [
          `Çerezler aracılığıyla işlenen kişisel verilerin veri sorumlusu ${BUSINESS_LEGAL.legalName}'tir (${BUSINESS_LEGAL.tradeName}). Ayrıntılı bilgi için KVKK Aydınlatma Metni'ni inceleyebilirsiniz.`,
        ],
      },
      {
        heading: 'Çerez nedir?',
        paragraphs: [
          'Çerezler ve benzeri teknolojiler (yerel depolama, oturum depolama), ziyaret ettiğiniz sitenin tarayıcınıza kaydettiği küçük veri dosyalarıdır. Sitenin çalışması, tercihlerinizin hatırlanması ve izin verirseniz sitenin nasıl kullanıldığının ölçülmesi için kullanılır.',
        ],
      },
      {
        heading: 'Zorunlu ve isteğe bağlı çerezler',
        paragraphs: [
          'Zorunlu çerezler; güvenlik, spam koruması, dil ve tema tercihi ile çerez tercihinizin saklanması gibi sitenin çalışması için gerekli işlevlerde kullanılır ve açık rızaya tabi değildir.',
          'Analitik ve pazarlama çerezleri yalnızca açık rıza vermeniz halinde yüklenir. "Reddet" ve "Kabul et" seçenekleri eşit şekilde sunulur; hiçbir seçim yapmamanız rıza sayılmaz.',
          'Tercihinizi dilediğiniz zaman sayfanın altındaki "Çerez Ayarları" bağlantısından değiştirebilirsiniz. Rızanın geri alınması, geri alma tarihinden sonraki işlemleri etkiler.',
        ],
      },
      {
        heading: 'Ödeme sayfası',
        paragraphs: [
          `Ödeme işlemleri ${PAYMENT_PROVIDER.brand} tarafından sağlanan güvenli ödeme sayfasında gerçekleşir. Bu sayfada kullanılan çerezler için ${PAYMENT_PROVIDER.legalName}'nin kendi çerez politikası geçerlidir.`,
        ],
      },
      {
        heading: 'Kullanılan çerezler',
        paragraphs: [
          'Sitede kullanılan çerezler ve benzeri teknolojiler aşağıdaki tabloda amaçları ve saklama süreleriyle birlikte listelenmiştir.',
          'Çerezleri tarayıcı ayarlarınızdan da silebilir veya engelleyebilirsiniz; zorunlu çerezlerin engellenmesi sitenin bazı işlevlerinin çalışmamasına yol açabilir.',
        ],
      },
    ];
  }

  return [
    {
      heading: 'Data controller',
      paragraphs: [
        `The controller for personal data processed through cookies is ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}).`,
      ],
    },
    {
      heading: 'What are cookies?',
      paragraphs: [
        'Cookies and similar technologies (local and session storage) are small data files a site stores in your browser to function, remember your preferences and, with your consent, measure how the site is used.',
      ],
    },
    {
      heading: 'Essential and optional cookies',
      paragraphs: [
        'Essential cookies support security, spam protection, language and theme preferences, and storing your cookie choice; they do not require consent.',
        'Analytics and marketing cookies load only after you consent. Reject and accept options are presented equally; silence is not consent. You can change your choice at any time via Cookie Settings at the bottom of the page.',
      ],
    },
    {
      heading: 'Payment page',
      paragraphs: [
        `Payments are completed on the secure payment page provided by ${PAYMENT_PROVIDER.brand}; ${PAYMENT_PROVIDER.legalName}'s own cookie policy applies there.`,
      ],
    },
    {
      heading: 'Cookies we use',
      paragraphs: [
        'The table below lists cookies and similar technologies used on this site with their purposes and retention periods. You can also delete or block cookies in your browser settings; blocking essential cookies may break some features.',
      ],
    },
  ];
}

export function getTermsSections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        heading: 'Taraflar ve kapsam',
        paragraphs: [
          `figtures.com, ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}) tarafından işletilir. Siteyi kullanan herkes bu koşulları kabul etmiş sayılır.`,
          'Figtures; özel yazılım, web sitesi, mobil uygulama, SaaS ve MVP geliştirme, UI/UX tasarım, yapay zekâ entegrasyonu ve teknik danışmanlık hizmetleri sunar. Hizmetler, her müşterinin ihtiyacına göre hazırlanan teklif üzerinden satılır.',
        ],
      },
      {
        heading: 'Teklif, sözleşme ve ödeme',
        paragraphs: [
          'Sitedeki örnek çalışmalar, hizmet açıklamaları ve hesaplama araçlarının sonuçları bilgilendirme amaçlıdır; bağlayıcı teklif veya kesin fiyat değildir. İletişim formu göndermek sözleşme kurmaz.',
          'Proje kapsamı, bedeli, ödeme planı, teslim takvimi ve fikrî haklar yazılı teklifte belirlenir. Teklifin onaylanıp ödemenin yapılmasıyla, tüketici işlemlerinde Mesafeli Satış Sözleşmesi, ticari müşterilerde ise taraflarca imzalanan proje sözleşmesi hükümleri uygulanır.',
          `Ödemeler ${PAYMENT_PROVIDER.brand} güvenli ödeme altyapısı üzerinden kredi kartı veya banka kartıyla ya da banka havalesi ve EFT ile yapılır. Tüm fiyatlar Türk lirası üzerinden gösterilir; KDV dahil olup olmadığı teklifte açıkça belirtilir.`,
        ],
      },
      {
        heading: 'Fikrî mülkiyet',
        bullets: [
          'Sitedeki metin, tasarım, logo ve diğer içerikler Figtures\'a aittir; izin alınmadan ticari amaçla kopyalanamaz veya çoğaltılamaz.',
          'Portföyde yer alan üçüncü taraf markaları ilgili hak sahiplerine aittir.',
          'Müşteri projelerindeki fikrî hakların devri ilgili sözleşmeye göre gerçekleşir.',
        ],
      },
      {
        heading: 'Kullanım kuralları',
        bullets: [
          'Siteye veya sistemlerine yetkisiz erişim girişiminde bulunulamaz.',
          'Formlar aracılığıyla hukuka aykırı, yanıltıcı veya zararlı içerik gönderilemez.',
          'Sitenin çalışmasını engelleyecek otomatik sorgu veya saldırı yapılamaz.',
        ],
      },
      {
        heading: 'Sorumluluk',
        paragraphs: [
          'Site içeriği özenle hazırlanır; ancak bilgilendirme amaçlı içeriklerin her duruma uygunluğu garanti edilmez. Sitede bağlantı verilen üçüncü taraf sitelerin içeriğinden Figtures sorumlu değildir.',
        ],
      },
      {
        heading: 'Uygulanacak hukuk ve yetki',
        paragraphs: [
          'Bu koşullar Türkiye Cumhuriyeti hukukuna tabidir.',
          'Tüketiciler, Ticaret Bakanlığınca her yıl ilan edilen parasal sınırlar dahilinde yerleşim yerlerindeki veya işlemin yapıldığı yerdeki Tüketici Hakem Heyetine, bu sınırların üzerindeki uyuşmazlıklarda Tüketici Mahkemesine başvurabilir.',
          'Ticari müşterilerle doğan uyuşmazlıklarda İstanbul Anadolu Mahkemeleri ve İcra Daireleri yetkilidir.',
          'Koşullarda yapılan değişiklikler bu sayfada yayımlandığı tarihten itibaren geçerlidir ve daha önce kurulmuş sözleşmeleri etkilemez.',
        ],
      },
    ];
  }

  return [
    {
      heading: 'Parties and scope',
      paragraphs: [
        `figtures.com is operated by ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}). By using the site you accept these terms.`,
        'Figtures provides custom software, web, mobile, SaaS and MVP development, UI/UX design, AI integration, and technical consulting. Services are sold through a proposal prepared for each client.',
      ],
    },
    {
      heading: 'Proposals, contracts, and payment',
      paragraphs: [
        'Portfolio examples, service descriptions, and calculator results are for information only and are not binding offers. Submitting a form does not create a contract.',
        'Scope, price, payment plan, timeline, and IP are set in the written proposal. Once the proposal is accepted and paid, the Distance Sales Agreement applies to consumers and the signed project agreement applies to business clients.',
        `Payments are made by credit or debit card through ${PAYMENT_PROVIDER.brand}, or by bank transfer. Prices are shown in Turkish lira; the proposal states whether VAT is included.`,
      ],
    },
    {
      heading: 'Intellectual property and acceptable use',
      bullets: [
        'Site content, design, and branding belong to Figtures and may not be copied for commercial use without permission.',
        'Third-party marks in the portfolio belong to their owners.',
        'Unauthorized access, unlawful form content, and automated attacks are prohibited.',
      ],
    },
    {
      heading: 'Governing law and jurisdiction',
      paragraphs: [
        'These terms are governed by the laws of the Republic of Türkiye. Consumers may apply to Consumer Arbitration Committees within the annual monetary limits, or to Consumer Courts above them. For business clients, Istanbul Anatolian Courts and Enforcement Offices have jurisdiction.',
        'Changes apply from publication on this page and do not affect contracts already concluded.',
      ],
    },
  ];
}

export function getDeliveryRefundSections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        heading: 'Hizmetin niteliği',
        paragraphs: [
          'Figtures; özel yazılım, web sitesi, mobil uygulama, SaaS ve MVP geliştirme, UI/UX tasarım, yapay zekâ entegrasyonu ve teknik danışmanlık hizmetlerini, müşteriye özel hazırlanan teklif üzerinden sunar.',
          'Hizmetin kapsamı, aşamaları, revizyon hakkı, bedeli, ödeme planı ve teslim takvimi, ödeme öncesinde müşteriye iletilen ve müşterinin onayladığı teklifte yer alır.',
        ],
      },
      {
        heading: 'Teslimat',
        paragraphs: [
          'Hizmetler dijital olarak teslim edilir; fiziksel ürün gönderimi ve kargo yoktur, bu nedenle kargo ücreti alınmaz.',
          'Teslimat; canlı web sitesi veya uygulamanın yayına alınması, test ortamı erişimi, kaynak kod deposu, tasarım dosyaları, dokümantasyon veya rapor olarak, müşterinin bildirdiği e-posta adresi ve teklifte belirtilen kanallar üzerinden yapılır.',
          'Teslim süresi teklifte belirtilen takvime göre işler. Takvim, müşterinin içerik, erişim ve onayları zamanında sağlamasına bağlıdır; müşteriden kaynaklanan gecikmeler takvimi aynı süre kadar uzatır. Figtures kaynaklı bir gecikme öngörüldüğünde müşteri yazılı olarak bilgilendirilir.',
          'Her teslimden sonra müşteri, teklifte belirtilen revizyon hakkı çerçevesinde değişiklik talep edebilir. Revizyonlar tamamlandıktan sonra teslim yazılı olarak (e-posta dahil) onaylanır.',
        ],
      },
      {
        heading: 'Tüketiciler için cayma hakkı',
        paragraphs: [
          `Tüketici, sözleşmenin kurulduğu günden itibaren 14 gün içinde hiçbir gerekçe göstermeden ve cezai şart ödemeden sözleşmeden cayabilir. Cayma bildirimi bu süre içinde ${LEGAL_CONTACT_EMAIL} adresine e-posta ile veya ${ADDR} adresine yazılı olarak iletilir.`,
          'Mesafeli Sözleşmeler Yönetmeliği m.15/1-h uyarınca, cayma süresi dolmadan tüketicinin açık onayıyla ifasına başlanan hizmetlerde cayma hakkı kullanılamaz. Tüketici bu onayı vermezse hizmete 14 günlük cayma süresi dolduktan sonra başlanır.',
        ],
      },
      {
        heading: 'Hizmet başlamadan iptal',
        paragraphs: [
          'Hizmete henüz başlanmamışsa müşteri siparişini iptal edebilir; ödenen tutarın tamamı iade edilir.',
        ],
      },
      {
        heading: 'Hizmet başladıktan sonra iptal',
        paragraphs: [
          'Hizmet başladıktan sonra iptal talebinde, iptal tarihine kadar tamamlanmış ve müşteriye teslim edilmiş aşamaların teklifteki bedeli ile müşterinin önceden onayladığı ve iadesi mümkün olmayan üçüncü taraf giderleri (alan adı, lisans, sunucu gibi) mahsup edilir; kalan tutar iade edilir.',
          'Tamamlanan aşamalara ait çıktılar müşteriye teslim edilir. Mahsup hesabı, aşama ve teslim kayıtlarıyla birlikte yazılı olarak müşteriye iletilir.',
        ],
      },
      {
        heading: 'Ayıplı hizmet',
        paragraphs: [
          'Teslim edilen hizmet teklifte belirtilen niteliklere uymuyorsa müşteri, 6502 sayılı Tüketicinin Korunması Hakkında Kanun m.57 uyarınca hizmetin yeniden görülmesini, ücretsiz onarımı, bedelden indirim yapılmasını veya sözleşmeden dönmeyi talep edebilir. Figtures, teslimden sonra bildirilen ve teklif kapsamına giren hataları ücretsiz düzeltir.',
        ],
      },
      {
        heading: 'İade süreci',
        paragraphs: [
          `İade veya iptal talebi ${LEGAL_CONTACT_EMAIL} adresine ad soyad (veya şirket unvanı), teklif veya proje adı ve ödeme tarihi belirtilerek iletilir. Kart numarası veya CVV bilgisi hiçbir zaman istenmez.`,
          'Cayma ve iptal halinde iade, talebin Figtures\'a ulaştığı tarihten itibaren en geç 14 gün içinde, ödemenin yapıldığı yöntemle ve tek seferde yapılır. Kartla yapılan ödemeler iyzico üzerinden aynı karta iade edilir; iadenin kart hesabına yansıma süresi kartı çıkaran bankaya bağlıdır. Taksitli ödemelerde iade, bankanın uygulamasına göre taksitler halinde karta yansıyabilir.',
          'Havale veya EFT ile yapılan ödemeler, müşterinin adına kayıtlı banka hesabına iade edilir.',
        ],
      },
      {
        heading: 'Ticari müşteriler',
        paragraphs: [
          'Tacir veya ticari amaçla hizmet alan müşterilerde tüketici cayma hakkı uygulanmaz. Bu müşterilerde iptal, fesih ve iade koşulları taraflarca imzalanan proje sözleşmesine göre belirlenir; sözleşmede hüküm bulunmayan hallerde bu sayfadaki iptal ve mahsup esasları uygulanır.',
        ],
      },
    ];
  }

  return [
    {
      heading: 'Nature of the service',
      paragraphs: [
        'Figtures provides custom software, web, mobile, SaaS and MVP development, UI/UX design, AI integration, and technical consulting through a proposal prepared for each client. Scope, milestones, revisions, price, payment plan, and timeline are set in the proposal the client approves before payment.',
      ],
    },
    {
      heading: 'Delivery',
      paragraphs: [
        'All services are delivered digitally; there is no physical shipment and no shipping fee.',
        'Deliverables (live site or app, staging access, source repository, design files, documentation, reports) are provided through the client’s email and the channels stated in the proposal, following the proposal timeline. Delays caused by missing client content, access, or approvals extend the timeline accordingly.',
      ],
    },
    {
      heading: 'Consumer right of withdrawal',
      paragraphs: [
        `Consumers may withdraw within 14 days of the contract date without giving reasons or paying a penalty, by notifying ${LEGAL_CONTACT_EMAIL} or ${ADDR_EN} in writing.`,
        'Under Art. 15/1-h of the Turkish Distance Contracts Regulation, the right of withdrawal does not apply to services started with the consumer’s explicit consent before the withdrawal period ends. Without that consent, work begins after the 14-day period.',
      ],
    },
    {
      heading: 'Cancellation',
      paragraphs: [
        'Before work starts: the full amount is refunded.',
        'After work starts: the proposal price of completed and delivered milestones and pre-approved non-refundable third-party costs (domain, licenses, servers) are deducted; the balance is refunded and completed deliverables are handed over.',
      ],
    },
    {
      heading: 'Refunds',
      paragraphs: [
        `Send refund requests to ${LEGAL_CONTACT_EMAIL} with your name or company, project, and payment date. Card numbers or CVV are never requested.`,
        'Refunds are made within 14 days of receiving the request, in one payment, using the original payment method. Card payments are refunded to the same card via iyzico; the time to appear on the statement depends on the issuing bank. Bank transfers are refunded to an account in the client’s name.',
      ],
    },
    {
      heading: 'Business clients',
      paragraphs: [
        'The consumer right of withdrawal does not apply to business clients. Cancellation, termination, and refunds follow the signed project agreement, and this page applies where the agreement is silent.',
      ],
    },
  ];
}

export function getDistanceSalesSections(lang: 'tr' | 'en'): LegalSection[] {
  if (lang === 'tr') {
    return [
      {
        id: 'preliminary',
        heading: 'Ön bilgilendirme formu',
        paragraphs: [
          'Bu form, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca, sözleşme kurulmadan ve ödeme yapılmadan önce alıcıyı bilgilendirmek amacıyla sunulur.',
        ],
        bullets: [
          `Satıcı / hizmet sağlayıcı: ${PROVIDER_LINE_TR}.`,
          'Hizmetin temel nitelikleri: Alıcıya özel hazırlanan ve alıcının onayladığı teklifte belirtilen yazılım, web sitesi, mobil uygulama, tasarım, yapay zekâ entegrasyonu veya danışmanlık hizmeti; kapsamı, aşamaları, teslim edilecek çıktıları ve revizyon hakkı teklifte yer alır.',
          'Toplam fiyat: Vergiler dahil toplam bedel, Türk lirası olarak teklifte ve ödeme sayfasında gösterilir. Ödeme onayından önce gösterilmeyen hiçbir ek ücret alıcıdan talep edilmez.',
          `Ödeme: ${PAYMENT_PROVIDER.brand} güvenli ödeme altyapısıyla kredi kartı veya banka kartı ile ya da banka havalesi ve EFT ile. Taksit seçenekleri ve varsa vade farkı ödeme sayfasında gösterilir.`,
          'Teslimat: Dijital olarak, teklifte belirtilen takvim ve kanallarla. Fiziksel gönderim ve kargo ücreti yoktur.',
          `Cayma hakkı: Sözleşmenin kurulduğu günden itibaren 14 gün. Bildirim ${LEGAL_CONTACT_EMAIL} adresine veya satıcının adresine yazılı olarak yapılır. Alıcının açık onayıyla cayma süresi dolmadan ifasına başlanan hizmetlerde cayma hakkı kullanılamaz (Mesafeli Sözleşmeler Yönetmeliği m.15/1-h).`,
          `Şikâyet ve itiraz: ${LEGAL_CONTACT_EMAIL} adresine veya satıcının adresine. Alıcı ayrıca Tüketici Hakem Heyetine ve Tüketici Mahkemesine başvurabilir.`,
        ],
      },
      {
        id: 'parties',
        heading: 'Madde 1: Taraflar',
        paragraphs: [
          `Satıcı / hizmet sağlayıcı ("Satıcı"): ${PROVIDER_LINE_TR}.`,
          'Alıcı ("Alıcı"): Teklifi onaylayan ve ödeme sayfasında adı soyadı, adresi, e-posta adresi ve telefon numarası yer alan gerçek veya tüzel kişi.',
        ],
      },
      {
        id: 'subject',
        heading: 'Madde 2: Sözleşmenin konusu',
        paragraphs: [
          'Bu sözleşmenin konusu, Alıcının figtures.com üzerinden veya Satıcının kendisine ilettiği ödeme bağlantısı aracılığıyla satın aldığı ve niteliği, kapsamı ve bedeli onaylı teklifte belirtilen hizmetin sunulmasına ilişkin tarafların hak ve yükümlülüklerinin belirlenmesidir.',
          'Onaylı teklif, ödeme sayfasındaki sipariş özeti ve bu sayfadaki ön bilgilendirme formu bu sözleşmenin ayrılmaz parçasıdır. Aralarında çelişki olması halinde onaylı teklif esas alınır; ancak Alıcının kanundan doğan hakları hiçbir durumda sınırlandırılamaz.',
        ],
      },
      {
        id: 'price',
        heading: 'Madde 3: Bedel ve ödeme',
        paragraphs: [
          'Hizmet bedeli, vergiler dahil olarak Türk lirası cinsinden teklifte ve ödeme sayfasında gösterilir. Bedel, teklifte belirtilen ödeme planına göre (örneğin başlangıçta ve teslimde olmak üzere aşamalar halinde) ödenir.',
          `Kartlı ödemeler Türkiye Cumhuriyet Merkez Bankası lisanslı ${PAYMENT_PROVIDER.legalName} (${PAYMENT_PROVIDER.brand}) altyapısı üzerinden 3D Secure doğrulamasıyla yapılır. Kart bilgileri Satıcı tarafından görülmez ve saklanmaz.`,
          'Taksitli ödemelerde taksit sayısı ve varsa vade farkı, kartı çıkaran bankanın koşullarına göre ödeme sayfasında gösterilir. Teklif kapsamı dışında kalan ek iş ve giderler ancak Alıcıya önceden yazılı olarak bildirilip Alıcı tarafından onaylanması halinde ücretlendirilir.',
          'Her ödeme için yasal mevzuata uygun fatura düzenlenir ve Alıcının e-posta adresine gönderilir.',
        ],
      },
      {
        id: 'performance',
        heading: 'Madde 4: Hizmetin ifası ve teslim',
        paragraphs: [
          'Satıcı, hizmeti teklifte belirtilen kapsam, kalite ve takvime uygun olarak ifa eder. Hizmet dijital olarak teslim edilir; teslimat canlı yayına alma, test ortamı erişimi, kaynak kod deposu, tasarım dosyaları veya dokümantasyon olarak, teklifte belirtilen kanallar ve Alıcının e-posta adresi üzerinden yapılır.',
          'Alıcı, hizmetin ifası için gereken içerik, erişim bilgileri ve onayları zamanında sağlamakla yükümlüdür. Alıcıdan kaynaklanan gecikmeler teslim takvimini aynı süre kadar uzatır.',
          'Hizmetin ifasının imkânsızlaşması halinde Satıcı, bu durumu öğrendiği tarihten itibaren 3 gün içinde Alıcıya yazılı olarak bildirir ve tahsil edilen tüm ödemeleri bildirim tarihinden itibaren en geç 14 gün içinde iade eder.',
        ],
      },
      {
        id: 'withdrawal',
        heading: 'Madde 5: Cayma hakkı',
        paragraphs: [
          'Alıcı tüketici ise, sözleşmenin kurulduğu günden itibaren 14 gün içinde hiçbir gerekçe göstermeden ve cezai şart ödemeden sözleşmeden cayabilir.',
          `Cayma hakkının kullanıldığına ilişkin bildirimin süresi içinde ${LEGAL_CONTACT_EMAIL} adresine e-posta ile veya ${ADDR} adresine yazılı olarak yapılması yeterlidir. Bildirimde ad soyad, teklif veya proje adı ve ödeme tarihinin belirtilmesi işlemi hızlandırır.`,
          'Satıcı, cayma bildiriminin kendisine ulaştığı tarihten itibaren en geç 14 gün içinde tahsil edilen tüm ödemeleri, Alıcının kullandığı ödeme aracına uygun şekilde, Alıcıya herhangi bir masraf yüklemeden ve tek seferde iade eder.',
        ],
      },
      {
        id: 'exceptions',
        heading: 'Madde 6: Cayma hakkının istisnası',
        paragraphs: [
          'Mesafeli Sözleşmeler Yönetmeliği m.15/1-h uyarınca, cayma hakkı süresi sona ermeden önce Alıcının onayı ile ifasına başlanan hizmetlerde cayma hakkı kullanılamaz.',
          'Satıcı, 14 günlük süre dolmadan hizmete başlamak için Alıcıdan ayrı ve açık bir onay alır ve bu onayın cayma hakkını sona erdireceğini Alıcıya önceden bildirir. Alıcı bu onayı vermezse hizmete cayma süresi dolduktan sonra başlanır. Onayın alındığına ilişkin kayıtlar Satıcı tarafından saklanır.',
        ],
      },
      {
        id: 'defects',
        heading: 'Madde 7: Ayıplı hizmet',
        paragraphs: [
          'Hizmetin teklifte belirtilen niteliklere uymaması halinde Alıcı, 6502 sayılı Kanun m.57 uyarınca hizmetin yeniden görülmesi, ücretsiz onarım, ayıp oranında bedelden indirim veya sözleşmeden dönme haklarından birini kullanabilir. Satıcı, teslimden sonra bildirilen ve teklif kapsamına giren hataları ücretsiz olarak giderir.',
        ],
      },
      {
        id: 'ip',
        heading: 'Madde 8: Fikrî mülkiyet',
        paragraphs: [
          'Proje için özel olarak üretilen tasarım, kaynak kod ve dokümantasyon üzerindeki mali haklar, bedelin tamamının ödenmesiyle birlikte teklifte belirtilen kapsamda Alıcıya devredilir.',
          'Projede kullanılan açık kaynak yazılımlar, yazı tipleri, görseller ve üçüncü taraf hizmetleri kendi lisans koşullarına tabidir. Satıcının birden fazla projede kullandığı genel araç ve kütüphaneler üzerindeki hakları saklıdır; Alıcıya bunları proje kapsamında süresiz kullanma hakkı tanınır.',
        ],
      },
      {
        id: 'data',
        heading: 'Madde 9: Kişisel veriler ve gizlilik',
        paragraphs: [
          'Alıcıya ait kişisel veriler KVKK Aydınlatma Metni ve Gizlilik Politikası kapsamında işlenir. Taraflar, sözleşme süresince ve sonrasında birbirlerinin ticari sırlarını ve gizli bilgilerini üçüncü kişilere açıklamaz.',
        ],
      },
      {
        id: 'disputes',
        heading: 'Madde 10: Uyuşmazlıkların çözümü',
        paragraphs: [
          'Bu sözleşmeden doğan uyuşmazlıklarda, Ticaret Bakanlığınca her yıl ilan edilen parasal sınırlar dahilinde Alıcının yerleşim yerindeki veya işlemin yapıldığı yerdeki Tüketici Hakem Heyetleri, bu sınırların üzerindeki uyuşmazlıklarda Tüketici Mahkemeleri yetkilidir.',
        ],
      },
      {
        id: 'formation',
        heading: 'Madde 11: Yürürlük',
        paragraphs: [
          'Alıcı, ödeme sayfasında bu sözleşmeyi ve ön bilgilendirme formunu okuduğunu ve kabul ettiğini onaylayıp ödemeyi tamamladığı anda sözleşme kurulur ve yürürlüğe girer. Sözleşmenin ve sipariş özetinin bir kopyası Alıcının e-posta adresine gönderilir ve Satıcı tarafından saklanır.',
          'Tacir veya ticari amaçla hizmet alan Alıcılar bakımından tüketici mevzuatına özgü cayma ve yetki hükümleri uygulanmaz; bu Alıcılarla taraflarca imzalanmış bir proje sözleşmesi varsa o sözleşme önceliklidir.',
        ],
      },
      {
        id: 'withdrawal-form',
        heading: 'Cayma formu örneği',
        paragraphs: [
          `Kime: ${BUSINESS_LEGAL.legalName} (${BUSINESS_LEGAL.tradeName}), ${ADDR}, ${LEGAL_CONTACT_EMAIL}`,
          'Bu formla aşağıda bilgileri yer alan hizmet sözleşmesinden cayma hakkımı kullandığımı bildiririm.',
          'Teklif veya proje adı / Sözleşme (ödeme) tarihi / Alıcının adı soyadı / Alıcının adresi / Tarih ve imza (yalnızca kâğıt üzerinde gönderiliyorsa).',
        ],
      },
    ];
  }

  return [
    {
      id: 'preliminary',
      heading: 'Pre-contract information',
      bullets: [
        `Seller / service provider: ${PROVIDER_LINE_EN}.`,
        'Service: the software, website, mobile app, design, AI integration, or consulting service described in the proposal prepared for and approved by the buyer, including scope, milestones, deliverables, and revisions.',
        'Total price: shown in Turkish lira including taxes in the proposal and on the payment page. No charge not shown before payment approval will be requested.',
        `Payment: by card via ${PAYMENT_PROVIDER.brand}, or by bank transfer. Installment options and any installment fee are shown on the payment page.`,
        'Delivery: digital, following the proposal timeline. No physical shipment or shipping fee.',
        `Withdrawal: 14 days from the contract date, by written notice to ${LEGAL_CONTACT_EMAIL}. The right does not apply to services started with the buyer’s explicit consent before the period ends (Distance Contracts Regulation Art. 15/1-h).`,
      ],
    },
    {
      id: 'terms',
      heading: 'Agreement summary',
      paragraphs: [
        'Parties: the seller above and the buyer named on the payment page. Subject: the service in the approved proposal; the proposal and order summary form part of this agreement.',
        `Payment: card payments are processed by ${PAYMENT_PROVIDER.legalName} with 3D Secure; card data is never seen or stored by the seller. An invoice is issued for every payment.`,
        'Performance: delivered digitally per the proposal timeline; delays caused by the buyer extend the timeline. If performance becomes impossible, the buyer is notified within 3 days and all payments are refunded within 14 days.',
        'Withdrawal and refunds: consumers may withdraw within 14 days; refunds are made within 14 days of notice, in one payment, to the original payment method. Work starts before the period ends only with separate explicit consent.',
        'Defects: consumers may request re-performance, free repair, a price reduction, or termination under Law No. 6502 Art. 57.',
        'Intellectual property: economic rights in project-specific design, code, and documentation transfer to the buyer upon full payment, as defined in the proposal. Third-party and open-source components follow their own licenses.',
        'Disputes: Consumer Arbitration Committees within the annual monetary limits, Consumer Courts above them.',
        'Formation: the agreement is concluded when the buyer accepts it on the payment page and completes payment; a copy is emailed to the buyer.',
        'The Turkish version of this agreement prevails.',
      ],
    },
  ];
}

export function getCorporateIdentityParagraph(lang: 'tr' | 'en'): string {
  if (lang === 'tr') {
    return `Figtures, özel yazılım geliştirme, dijital ürün tasarımı, yapay zekâ entegrasyonu ve teknik danışmanlık sunan bir ticari markadır. Hizmet sağlayıcı ${BUSINESS_LEGAL.legalName}’tir. ${formatBusinessLegalFooter('tr')}`;
  }
  return `Figtures is a trademark for custom software, product design, AI integration, and technical consulting. The service provider is ${BUSINESS_LEGAL.legalName}. ${formatBusinessLegalFooter('en')}`;
}
